import React, { useEffect, useRef, useState } from 'react';

import { LoadingIndicator } from '@motorway/mw-highway-code/alpha';

import { setRawCookie } from 'Utilities';
import { GENERIC_ANALYTICS_EVENTS } from 'Utilities/analytics/analyticsGenericEvents';
import { EVENT } from 'Utilities/analytics/optimizely/constants';
import { OPTIMIZELY_OPT_OUT_COOKIE } from 'Utilities/analytics/optimizely/optimizely';
import { useOptimizelyTrack } from 'Utilities/analytics/optimizely/useOptimizelyTrack';

import { toggleSnowplowCookies } from '../../services/privateApis';
import { postConsentDecision } from '../CookieBanner/consentService';
import {
  CONSENT_COOKIE_EXPIRY_DAYS,
} from '../CookieBanner/cookieConsent.constants';
import { applyConsent, getStoredConsent, useConsentChangeListener } from '../CookieBanner/cookieConsent.helpers';
import type { ConsentObject } from '../CookieBanner/cookieConsent.types';

import { CookiePreferences } from './CookiePreferences';

export const CookiePreferencesCentre = () => {
  const { OPTIMIZELY_TRACK } = useOptimizelyTrack();

  const [mounted, setMounted] = useState(false);
  const isUpdatingRef = useRef(false);
  const [consent, setConsent] = useState<ConsentObject>(getStoredConsent);

  useEffect(() => {
    setMounted(true);
  }, []);

  useConsentChangeListener(() => {
    setConsent(getStoredConsent());
  });

  // After a page reload following an analytics opt-out, the collector's
  // Set-Cookie from the final tracking event may have re-set the sp cookie
  // to a real UUID. Stamp the placeholder back once the page has loaded.
  useEffect(() => {
    if (consent.analytics_storage === 'denied') {
      toggleSnowplowCookies(false)?.catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted) {
    return (
      <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '275px' }}>
        <LoadingIndicator size="large" />
      </div>
    );
  }

  const updateConsent = (updatedConsent: ConsentObject) => {
    if (isUpdatingRef.current) {
      return;
    }
    isUpdatingRef.current = true;

    const persisted = applyConsent(updatedConsent);
    setConsent(updatedConsent);
    postConsentDecision(updatedConsent, 'preference_centre');

    if (persisted) {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      isUpdatingRef.current = false;
    }
  };

  const handleMarketingToggle = () => {
    const isCurrentlyGranted = consent.ad_storage === 'granted';
    const newValue = isCurrentlyGranted ? 'denied' : 'granted';

    GENERIC_ANALYTICS_EVENTS.TOGGLE_CLICK_EVENT({
      category: 'cookie_preferences',
      name: 'marketing',
      value: !isCurrentlyGranted,
    });

    OPTIMIZELY_TRACK(EVENT.COOKIE_PREFERENCE_CENTRE_MARKETING_TOGGLED, { consent: newValue });

    updateConsent({
      ...consent,
      ad_personalization: newValue,
      ad_storage: newValue,
      ad_user_data: newValue,
      personalization_storage: newValue,
    });
  };

  const handleAnalyticsToggle = () => {
    const isCurrentlyGranted = consent.analytics_storage === 'granted';
    const newValue = isCurrentlyGranted ? 'denied' : 'granted';

    GENERIC_ANALYTICS_EVENTS.TOGGLE_CLICK_EVENT({
      category: 'cookie_preferences',
      name: 'analytics',
      value: !isCurrentlyGranted,
    });

    OPTIMIZELY_TRACK(EVENT.COOKIE_PREFERENCE_CENTRE_ANALYTICS_TOGGLED, { consent: newValue });

    if (isCurrentlyGranted) {
      setRawCookie(OPTIMIZELY_OPT_OUT_COOKIE, 'true', CONSENT_COOKIE_EXPIRY_DAYS);
    } else {
      setRawCookie(OPTIMIZELY_OPT_OUT_COOKIE, '', -1);
      // Clear the placeholder cookies so Snowplow can regenerate fresh ones
      toggleSnowplowCookies(true)?.catch(() => {});
    }

    updateConsent({
      ...consent,
      analytics_storage: newValue,
    });
  };

  const marketingEnabled = consent.ad_storage === 'granted';
  const analyticsEnabled = consent.analytics_storage === 'granted';

  return (
    <CookiePreferences
      analyticsEnabled={analyticsEnabled}
      marketingEnabled={marketingEnabled}
      onAnalyticsToggle={handleAnalyticsToggle}
      onMarketingToggle={handleMarketingToggle}
    />
  );
};
